import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { HiUser, HiShoppingBag } from 'react-icons/hi';
import './ReceiveWorkOrderModal.css';

const SIZE_OPTIONS = [
  { value: '', label: 'One Size' },
  { value: 'S', label: 'S' },
  { value: 'M', label: 'M' },
  { value: 'L', label: 'L' },
  { value: 'XL', label: 'XL' },
  { value: 'XXL', label: 'XXL' },
  { value: 'XXXL', label: 'XXXL' }
];

const ReceiveWorkOrderModal = ({
  isOpen,
  onClose,
  onConfirm,
  workOrder,
  defaultQuantity = 0,
  defaultBillAmount = 0,
  apiBase = '',
  businessId = null,
  branchId = null
}) => {
  const [sizeBreakdown, setSizeBreakdown] = useState([{ size: '', quantity: '', barcode: '' }]);
  const [billAmount, setBillAmount] = useState('');
  const [costPerItem, setCostPerItem] = useState('');
  const [costPerItemStitch, setCostPerItemStitch] = useState('');
  const [outputMode, setOutputMode] = useState('existing'); // 'existing' | 'new'
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedExistingProduct, setSelectedExistingProduct] = useState(null);
  const [outputProductName, setOutputProductName] = useState('');
  const [outputProductCode, setOutputProductCode] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showOutputProductOverride, setShowOutputProductOverride] = useState(false); // true = user chose "different product", show full options
  const searchTimeoutRef = useRef(null);
  const searchDropdownRef = useRef(null);

  const bid = businessId || workOrder?.business_id || workOrder?.businessId;

  const fetchProductSearch = useCallback(async (q) => {
    if (!apiBase || !bid || !q || String(q).trim().length < 2) {
      setProductSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({ q: String(q).trim(), productType: 'stitched', limit: 15 });
      if (branchId) params.set('branchId', branchId);
      const res = await axios.get(`${apiBase}/products/business/${bid}/search?${params.toString()}`);
      const list = res.data?.products || [];
      setProductSearchResults(list.filter((p) => (p.product_type || p.productType) === 'stitched'));
    } catch (err) {
      setProductSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [apiBase, bid, branchId]);

  useEffect(() => {
    if (!isOpen) return;
    if (outputMode === 'existing' && productSearchQuery.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => fetchProductSearch(productSearchQuery), 300);
      return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }
    setProductSearchResults([]);
  }, [isOpen, outputMode, productSearchQuery, fetchProductSearch]);

  useEffect(() => {
    if (!isOpen || !workOrder) return;
    const meta = typeof workOrder?.meta === 'object' && workOrder.meta !== null
      ? workOrder.meta
      : (typeof workOrder?.meta === 'string' ? (() => { try { return JSON.parse(workOrder.meta); } catch { return {}; } })() : {});
    const defaultQty = defaultQuantity > 0 ? String(defaultQuantity) : '';
    setSizeBreakdown([{ size: '', quantity: defaultQty, barcode: '' }]);
    setBillAmount(defaultBillAmount.toString());
    const receipts = meta.receipts || [];
    const refCost = meta.referenceCostPerItem ?? meta.reference_cost_per_item;
    const refStitch = meta.referenceCostPerItemStitch ?? meta.reference_cost_per_item_stitch;
    if (receipts.length > 0 && (refCost != null || refStitch != null)) {
      setCostPerItem(refCost != null ? String(refCost) : '');
      setCostPerItemStitch(refStitch != null ? String(refStitch) : '');
    } else {
      setCostPerItem('');
      setCostPerItemStitch('');
    }
    setProductSearchQuery('');
    setProductSearchResults([]);
    setErrors({});
    const linkedOutput = workOrder?.output_product ?? workOrder?.outputProduct;
    const outputProductId = workOrder?.output_product_id ?? workOrder?.outputProductId ?? linkedOutput?.id;
    const plannedName = meta.plannedOutputProductName ?? meta.planned_output_product_name;
    const plannedCode = meta.plannedOutputProductCode ?? meta.planned_output_product_code;
    if (linkedOutput?.id || outputProductId) {
      setOutputMode('existing');
      const productId = linkedOutput?.id ?? outputProductId;
      setSelectedExistingProduct({
        id: productId,
        name: linkedOutput?.name ?? linkedOutput?.productName ?? (linkedOutput ? null : `Product #${productId}`),
        product_code: linkedOutput?.product_code ?? linkedOutput?.productCode ?? ''
      });
      setOutputProductName('');
      setOutputProductCode('');
    } else if (plannedName && String(plannedName).trim()) {
      setOutputMode('new');
      setSelectedExistingProduct(null);
      setOutputProductName(String(plannedName).trim());
      setOutputProductCode(plannedCode ? String(plannedCode).trim() : '');
    } else {
      setOutputMode('existing');
      setSelectedExistingProduct(null);
      setOutputProductName('');
      setOutputProductCode('');
    }
    setShowOutputProductOverride(false);
  }, [isOpen, defaultQuantity, defaultBillAmount, workOrder?.id, workOrder?.output_product?.id, workOrder?.output_product_id, workOrder?.outputProduct?.id, workOrder?.outputProductId, workOrder?.meta]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target)) {
        setProductSearchResults([]);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Auto-calculate bill amount for this batch: Stitch Price Per Item × total quantity received
  useEffect(() => {
    if (!isOpen) return;
    const totalQty = sizeBreakdown.reduce((s, r) => s + (parseInt(r.quantity, 10) || 0), 0);
    const stitchPrice = parseFloat(costPerItemStitch);
    if (totalQty > 0 && !isNaN(stitchPrice) && stitchPrice >= 0) {
      const calculated = (stitchPrice * totalQty).toFixed(2);
      setBillAmount(calculated);
    }
  }, [isOpen, costPerItemStitch, sizeBreakdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    const validRows = sizeBreakdown.filter(
      (r) => (r.size !== undefined && r.size !== null) || (parseInt(r.quantity, 10) || 0) > 0
    );
    const totalQty = validRows.reduce((s, r) => s + (parseInt(r.quantity, 10) || 0), 0);
    if (totalQty <= 0) {
      errors.sizeBreakdown = 'Add at least one size with quantity greater than 0';
    }

    const amount = parseFloat(billAmount);
    if (billAmount === '' || isNaN(amount) || amount < 0) {
      errors.billAmount = 'Please enter a valid bill amount (0 or greater)';
    }

    const costPerItemNum = costPerItem === '' ? NaN : parseFloat(costPerItem);
    const costPerItemStitchNum = costPerItemStitch === '' ? NaN : parseFloat(costPerItemStitch);
    if (costPerItem === '' || isNaN(costPerItemNum) || costPerItemNum < 0) {
      errors.costPerItem = 'Enter cost per item (0 or greater). Used with stitch cost as product cost per piece.';
    }
    if (costPerItemStitch === '' || isNaN(costPerItemStitchNum) || costPerItemStitchNum < 0) {
      errors.costPerItemStitch = 'Enter cost per item stitch (0 or greater). Used with cost per item as product cost per piece.';
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    const breakdown = sizeBreakdown
      .filter((r) => (parseInt(r.quantity, 10) || 0) > 0)
      .map((r) => ({
        size: (r.size && String(r.size).trim()) || null,
        quantity: parseInt(r.quantity, 10) || 0,
        barcode: (r.barcode && String(r.barcode).trim()) ? String(r.barcode).trim() : undefined
      }));

    const totalQtyNum = breakdown.reduce((s, r) => s + r.quantity, 0);
    const costPerItemVal = parseFloat(costPerItem) || 0;
    const costPerItemStitchVal = parseFloat(costPerItemStitch) || 0;
    const payload = {
      sizeBreakdown: breakdown,
      receivedQuantity: totalQtyNum,
      billAmount: parseFloat(amount),
      costPerItem: costPerItemVal,
      costPerItemStitch: costPerItemStitchVal
    };
    if (selectedExistingProduct?.id) {
      payload.outputProductId = selectedExistingProduct.id;
    } else {
      if (outputProductName.trim()) payload.outputProductName = outputProductName.trim();
      if (outputProductCode.trim()) payload.outputProductCode = outputProductCode.trim();
    }

    setSubmitting(true);
    try {
      const result = onConfirm(payload);
      if (result && typeof result.then === 'function') {
        await result;
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const meta = typeof workOrder?.meta === 'object' && workOrder?.meta !== null
    ? workOrder.meta
    : (typeof workOrder?.meta === 'string' ? (() => { try { return JSON.parse(workOrder.meta); } catch { return {}; } })() : {});
  const plannedOutputName = meta.plannedOutputProductName ?? meta.planned_output_product_name;
  const plannedOutputCode = meta.plannedOutputProductCode ?? meta.planned_output_product_code;
  const hasPlannedProduct = plannedOutputName && String(plannedOutputName).trim();
  const hasLinkedOutput = !!(workOrder?.output_product?.id ?? workOrder?.output_product_id ?? workOrder?.outputProduct?.id ?? workOrder?.outputProductId);

  return (
    <div className="receive-modal-overlay" onClick={() => !submitting && onClose()}>
      <div className="receive-modal" onClick={(e) => e.stopPropagation()}>
        <div className="receive-modal-header">
          <h3 className="receive-modal-title">Receive from Tailor</h3>
          <button type="button" className="receive-modal-close" onClick={onClose} disabled={submitting} aria-label="Close">×</button>
        </div>

        <div className="receive-modal-content">
          {workOrder && (
            <div className="receive-modal-info">
              <p className="receive-modal-work-order">
                Work Order: <strong>{workOrder.work_order_number}</strong>
              </p>
              {workOrder.input_quantity != null && (
                <p className="receive-modal-quantity-info">
                  Input Quantity: <strong>{workOrder.input_quantity}</strong>
                </p>
              )}
              {workOrder.expected_output_quantity != null && workOrder.expected_output_quantity > 0 && (
                <p className="receive-modal-expected-info">
                  Expected: <strong>{workOrder.expected_output_quantity}</strong> pieces
                  {(workOrder.received_output_quantity ?? 0) > 0 && (
                    <> · Already received: <strong>{workOrder.received_output_quantity}</strong> · Remaining: <strong>{Math.max(0, workOrder.expected_output_quantity - (workOrder.received_output_quantity || 0))}</strong></>
                  )}
                </p>
              )}
              {workOrder.notes && (
                <div className="receive-modal-notes">
                  <span className="receive-modal-notes-label">Notes:</span>
                  <span className="receive-modal-notes-text">{workOrder.notes}</span>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="receive-modal-form">
            <div className="receive-form-group">
              <label className="receive-form-label">
                Quantities received per size <span className="required">*</span>
              </label>
              <p className="receive-form-hint">Enter how many units you received for each size. Add barcode if needed (optional).</p>
              <div className="receive-size-breakdown-list">
                {sizeBreakdown.map((row, idx) => {
                  const usedByOtherRows = sizeBreakdown
                    .filter((_, i) => i !== idx)
                    .map((r) => (r.size ?? '').trim())
                    .filter(Boolean);
                  const sizeOptionsForRow = SIZE_OPTIONS.filter(
                    (opt) => (opt.value || '') === (row.size ?? '') || !usedByOtherRows.includes(opt.value || '')
                  );
                  return (
                  <div key={idx} className="receive-size-breakdown-card">
                    <select
                      className="receive-form-input receive-size-select"
                      value={row.size ?? ''}
                      onChange={(e) => {
                        const next = [...sizeBreakdown];
                        next[idx] = { ...next[idx], size: e.target.value };
                        setSizeBreakdown(next);
                      }}
                      aria-label="Size"
                    >
                      {sizeOptionsForRow.map((opt) => (
                        <option key={opt.value || '__one__'} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Qty"
                      className="receive-form-input receive-qty-input"
                      value={row.quantity ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const next = [...sizeBreakdown];
                        if (raw === '') {
                          next[idx] = { ...next[idx], quantity: '' };
                        } else {
                          const num = parseInt(raw, 10);
                          next[idx] = { ...next[idx], quantity: isNaN(num) ? '' : String(Math.max(0, num)) };
                        }
                        setSizeBreakdown(next);
                        if (errors.sizeBreakdown) setErrors({ ...errors, sizeBreakdown: null });
                      }}
                      aria-label="Quantity (whole numbers only)"
                    />
                    <input
                      type="text"
                      placeholder="Barcode (optional)"
                      className="receive-form-input receive-barcode-input"
                      value={row.barcode ?? ''}
                      onChange={(e) => {
                        const next = [...sizeBreakdown];
                        next[idx] = { ...next[idx], barcode: e.target.value };
                        setSizeBreakdown(next);
                      }}
                      maxLength={100}
                      aria-label="Barcode"
                    />
                    <button
                      type="button"
                      className="receive-remove-size-btn"
                      onClick={() => sizeBreakdown.length > 1 && setSizeBreakdown(sizeBreakdown.filter((_, i) => i !== idx))}
                      title={sizeBreakdown.length <= 1 ? 'At least one row required' : 'Remove row'}
                      disabled={sizeBreakdown.length <= 1}
                    >
                      ×
                    </button>
                  </div>
                  );
                })}
                <button
                  type="button"
                  className="receive-add-size-btn"
                  onClick={() => setSizeBreakdown([...sizeBreakdown, { size: '', quantity: '', barcode: '' }])}
                >
                  + Add size
                </button>
              </div>
              {sizeBreakdown.length > 0 && (
                <div className="receive-size-total">
                  Total received: <strong>{sizeBreakdown.reduce((s, r) => s + (parseInt(r.quantity, 10) || 0), 0)}</strong> units
                </div>
              )}
              {errors.sizeBreakdown && (
                <span className="receive-form-error">{errors.sizeBreakdown}</span>
              )}
            </div>

            <div className="receive-form-group">
              <label className="receive-form-label">Output product (inventory)</label>
              {(hasLinkedOutput || hasPlannedProduct) && !showOutputProductOverride ? (
                <>
                  <div className="receive-selected-product receive-linked-product">
                    <span className="receive-selected-label">Product set at work order creation (you can change it below if needed):</span>
                    <strong>
                      {hasLinkedOutput
                        ? (selectedExistingProduct?.name ?? workOrder?.output_product?.name ?? workOrder?.outputProduct?.name)
                        : (outputProductName || plannedOutputName)}
                    </strong>
                    {(hasLinkedOutput
                      ? (selectedExistingProduct?.product_code ?? workOrder?.output_product?.product_code ?? workOrder?.outputProduct?.productCode)
                      : (outputProductCode || plannedOutputCode)) && (
                      <span className="receive-selected-code">
                        {' '}
                        ({hasLinkedOutput
                          ? (selectedExistingProduct?.product_code ?? workOrder?.output_product?.product_code ?? workOrder?.outputProduct?.productCode)
                          : (outputProductCode || plannedOutputCode)})
                      </span>
                    )}
                    <p className="receive-form-hint" style={{ marginTop: '6px', marginBottom: 0 }}>
                      {hasLinkedOutput
                        ? 'Receiving into this product. Inventory will be added here.'
                        : 'A new product will be created with this name and code when you receive.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="receive-different-product-btn"
                    onClick={() => setShowOutputProductOverride(true)}
                  >
                    Receive into a different product
                  </button>
                </>
              ) : (hasLinkedOutput || hasPlannedProduct) && showOutputProductOverride ? (
                <>
                  <p className="receive-form-hint" style={{ marginBottom: '8px' }}>
                    Choose a different product to add received pieces to:
                  </p>
                  <div className="receive-output-mode-tabs">
                    <button
                      type="button"
                      className={`receive-mode-tab ${outputMode === 'existing' ? 'active' : ''}`}
                      onClick={() => { setOutputMode('existing'); setSelectedExistingProduct(null); setOutputProductName(''); setOutputProductCode(''); setProductSearchResults([]); }}
                    >
                      Add to existing product
                    </button>
                    <button
                      type="button"
                      className={`receive-mode-tab ${outputMode === 'new' ? 'active' : ''}`}
                      onClick={() => { setOutputMode('new'); setSelectedExistingProduct(null); setProductSearchQuery(''); setProductSearchResults([]); }}
                    >
                      Create new product
                    </button>
                  </div>
                  {outputMode === 'existing' && (
                    <div className="receive-existing-product-wrap" ref={searchDropdownRef}>
                      {selectedExistingProduct ? (
                        <div className="receive-selected-product">
                          <span className="receive-selected-label">Selected:</span>
                          <strong>{selectedExistingProduct.name}</strong>
                          {selectedExistingProduct.product_code && <span className="receive-selected-code"> ({selectedExistingProduct.product_code})</span>}
                          <button type="button" className="receive-clear-product-btn" onClick={() => setSelectedExistingProduct(null)}>Change</button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="receive-form-input"
                            value={productSearchQuery}
                            onChange={(e) => setProductSearchQuery(e.target.value)}
                            placeholder="Search by product name or code (min 2 characters)"
                            maxLength={100}
                            aria-label="Search existing product"
                          />
                          {searchLoading && <div className="receive-search-loading">Searching…</div>}
                          {productSearchResults.length > 0 && (
                            <ul className="receive-product-search-dropdown">
                              {productSearchResults.map((p) => (
                                <li key={p.id}>
                                  <button
                                    type="button"
                                    className="receive-product-search-item"
                                    onClick={() => {
                                      setSelectedExistingProduct(p);
                                      setProductSearchQuery('');
                                      setProductSearchResults([]);
                                    }}
                                  >
                                    <span className="receive-product-search-name">{p.name || 'Unnamed'}</span>
                                    {p.product_code && <span className="receive-product-search-code">{p.product_code}</span>}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {!searchLoading && productSearchQuery.trim().length >= 2 && productSearchResults.length === 0 && (
                            <div className="receive-form-hint">No stitched products found. Use &quot;Create new product&quot; to add one.</div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {outputMode === 'new' && (
                    <div className="receive-output-product-row">
                      <div className="receive-form-field">
                        <label htmlFor="outputProductNameOverride" className="receive-form-sublabel">Product name</label>
                        <input
                          id="outputProductNameOverride"
                          type="text"
                          className="receive-form-input"
                          value={outputProductName}
                          onChange={(e) => setOutputProductName(e.target.value)}
                          placeholder="e.g. Men's Formal Shirt"
                          maxLength={100}
                        />
                      </div>
                      <div className="receive-form-field">
                        <label htmlFor="outputProductCodeOverride" className="receive-form-sublabel">Product code (optional)</label>
                        <input
                          id="outputProductCodeOverride"
                          type="text"
                          className="receive-form-input"
                          value={outputProductCode}
                          onChange={(e) => setOutputProductCode(e.target.value)}
                          placeholder="e.g. SHIRT-001"
                          maxLength={50}
                        />
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="receive-different-product-btn receive-use-default-btn"
                    onClick={() => setShowOutputProductOverride(false)}
                  >
                    Use product from work order creation
                  </button>
                </>
              ) : (
                <>
                  <p className="receive-form-hint">Search for an existing stitched product by name or code to add received pieces to it, or create a new product.</p>
                  <div className="receive-output-mode-tabs">
                    <button
                      type="button"
                      className={`receive-mode-tab ${outputMode === 'existing' ? 'active' : ''}`}
                      onClick={() => { setOutputMode('existing'); setSelectedExistingProduct(null); setOutputProductName(''); setOutputProductCode(''); setProductSearchResults([]); }}
                    >
                      Add to existing product
                    </button>
                    <button
                      type="button"
                      className={`receive-mode-tab ${outputMode === 'new' ? 'active' : ''}`}
                      onClick={() => { setOutputMode('new'); setSelectedExistingProduct(null); setProductSearchQuery(''); setProductSearchResults([]); }}
                    >
                      Create new product
                    </button>
                  </div>

                  {outputMode === 'existing' && (
                    <div className="receive-existing-product-wrap" ref={searchDropdownRef}>
                      {selectedExistingProduct ? (
                        <div className="receive-selected-product">
                          <span className="receive-selected-label">Selected:</span>
                          <strong>{selectedExistingProduct.name}</strong>
                          {selectedExistingProduct.product_code && <span className="receive-selected-code"> ({selectedExistingProduct.product_code})</span>}
                          <button type="button" className="receive-clear-product-btn" onClick={() => setSelectedExistingProduct(null)}>Change</button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="receive-form-input"
                            value={productSearchQuery}
                            onChange={(e) => setProductSearchQuery(e.target.value)}
                            placeholder="Search by product name or code (min 2 characters)"
                            maxLength={100}
                            aria-label="Search existing product"
                          />
                          {searchLoading && <div className="receive-search-loading">Searching…</div>}
                          {productSearchResults.length > 0 && (
                            <ul className="receive-product-search-dropdown">
                              {productSearchResults.map((p) => (
                                <li key={p.id}>
                                  <button
                                    type="button"
                                    className="receive-product-search-item"
                                    onClick={() => {
                                      setSelectedExistingProduct(p);
                                      setProductSearchQuery('');
                                      setProductSearchResults([]);
                                    }}
                                  >
                                    <span className="receive-product-search-name">{p.name || 'Unnamed'}</span>
                                    {p.product_code && <span className="receive-product-search-code">{p.product_code}</span>}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {!searchLoading && productSearchQuery.trim().length >= 2 && productSearchResults.length === 0 && (
                            <div className="receive-form-hint">No stitched products found. Use &quot;Create new product&quot; to add one.</div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {outputMode === 'new' && (
                    <div className="receive-output-product-row">
                      <div className="receive-form-field">
                        <label htmlFor="outputProductNameNew" className="receive-form-sublabel">Product name</label>
                        <input
                          id="outputProductNameNew"
                          type="text"
                          className="receive-form-input"
                          value={outputProductName}
                          onChange={(e) => setOutputProductName(e.target.value)}
                          placeholder="e.g. Men's Formal Shirt"
                          maxLength={100}
                        />
                      </div>
                      <div className="receive-form-field">
                        <label htmlFor="outputProductCodeNew" className="receive-form-sublabel">Product code</label>
                        <input
                          id="outputProductCodeNew"
                          type="text"
                          className="receive-form-input"
                          value={outputProductCode}
                          onChange={(e) => setOutputProductCode(e.target.value)}
                          placeholder="e.g. SHIRT-001"
                          maxLength={50}
                        />
                      </div>
                      <p className="receive-form-hint">Leave blank to use default (e.g. Input Name (Our Creation)).</p>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="receive-form-group">
              <label className="receive-form-label">Cost per piece (first batch sets reference for later batches)</label>
              <div className="receive-cost-per-piece-row">
                <div className="receive-cost-field receive-cost-field-with-sticker">
                  <div className="receive-field-sticker receive-field-sticker-bag" title="Bag, button prices, etc." aria-hidden>
                    <HiShoppingBag className="receive-field-sticker-icon" />
                    <span className="receive-field-sticker-label">Bag, buttons</span>
                  </div>
                  <label htmlFor="costPerItem" className="receive-form-sublabel">Cost per item (PKR) <span className="required">*</span></label>
                  <input
                    id="costPerItem"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`receive-form-input ${errors.costPerItem ? 'error' : ''}`}
                    value={costPerItem}
                    onChange={(e) => {
                      setCostPerItem(e.target.value);
                      if (errors.costPerItem) setErrors((err) => ({ ...err, costPerItem: null }));
                    }}
                    placeholder="e.g. bag, buttons"
                  />
                  <span className="receive-form-hint">e.g. bag, button prices, thread, etc.</span>
                  {errors.costPerItem && <span className="receive-form-error">{errors.costPerItem}</span>}
                </div>
                <div className="receive-cost-field receive-cost-field-with-sticker">
                  <div className="receive-field-sticker receive-field-sticker-tailor" title="Tailor / stitch cost" aria-hidden>
                    <HiUser className="receive-field-sticker-icon" />
                    <span className="receive-field-sticker-label">Tailor</span>
                  </div>
                  <label htmlFor="costPerItemStitch" className="receive-form-sublabel">Stitch Price Per Item (PKR) <span className="required">*</span></label>
                  <input
                    id="costPerItemStitch"
                    type="number"
                    min="0"
                    step="0.01"
                    className={`receive-form-input ${errors.costPerItemStitch ? 'error' : ''}`}
                    value={costPerItemStitch}
                    onChange={(e) => {
                      setCostPerItemStitch(e.target.value);
                      if (errors.costPerItemStitch) setErrors((err) => ({ ...err, costPerItemStitch: null }));
                    }}
                    placeholder="e.g. 50"
                  />
                  {errors.costPerItemStitch && <span className="receive-form-error">{errors.costPerItemStitch}</span>}
                </div>
              </div>
              {(costPerItem !== '' || costPerItemStitch !== '') && (
                <p className="receive-form-hint receive-total-cost-hint">
                  Total cost per piece: <strong>PKR {((parseFloat(costPerItem) || 0) + (parseFloat(costPerItemStitch) || 0)).toFixed(2)}</strong> (used as product cost in inventory)
                </p>
              )}
              <span className="receive-form-hint">On first receive, enter both; they are saved as reference and pre-filled for later batches. You can still edit for each batch.</span>
            </div>

            <div className="receive-form-group">
              <label htmlFor="billAmount" className="receive-form-label">
                Bill Amount (PKR) <span className="required">*</span>
              </label>
              <div className="receive-input-wrapper">
                <span className="receive-currency">PKR</span>
                <input
                  id="billAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  readOnly
                  className={`receive-form-input receive-form-input-with-currency receive-form-input-readonly ${errors.billAmount ? 'error' : ''}`}
                  value={billAmount}
                  placeholder="0.00"
                  aria-readonly="true"
                />
              </div>
              <span className="receive-form-hint">Auto-calculated as Stitch Price Per Item × quantity received.</span>
              {errors.billAmount && (
                <span className="receive-form-error">{errors.billAmount}</span>
              )}
            </div>

            <div className="receive-modal-actions">
              <button
                type="button"
                className="receive-modal-button receive-modal-button-cancel"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="receive-modal-button receive-modal-button-confirm"
                disabled={submitting}
              >
                {submitting ? 'Receiving...' : 'Receive'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReceiveWorkOrderModal;
